// Why a submission was made, as a closed vocabulary.
//
// Both lists exist to stop the queue filling with bare certificate numbers.
// The genuine list deliberately has no "Other" escape hatch: a claim asks us to
// clear a number and publish the owner's photo, and the only claims worth that
// are the ones backed by a document somebody else issued. "I own it and it looks
// real to me" is a request for free authentication, not a claim.
//
// Shared by the two public forms, the admin review drawer, and the reply prompt,
// so the label the reviewer reads is the label the reporter picked.

export interface ReportReasonOption {
    value: string;
    label: string;
    /** Shown under the select once the option is chosen. */
    hint: string;
}

export const GENUINE_CLAIM_REASONS: readonly ReportReasonOption[] = [
    {
        value: 'loa_upgrade',
        label: 'The item was upgraded to a full LOA',
        hint: 'Enter the upgraded certificate number and photograph the letter itself.',
    },
    {
        value: 'original_invoice',
        label: 'I am the original recipient of the certificate',
        hint: 'Upload the invoice or receipt the authenticator issued to you.',
    },
    {
        value: 'expert_review',
        label: 'A positive written review from Steve Grad',
        hint: 'Upload the written opinion or the screenshot of it. A verbal opinion is not enough.',
    },
    {
        value: 'auction_invoice',
        label: 'Bought at a major auction house, with invoice and lot number',
        hint: 'Add the lot link, or a second photo of the auction invoice, plus the lot number.',
    },
];

export const FAKE_REPORT_REASONS: readonly ReportReasonOption[] = [
    {
        value: 'sticker_or_label',
        label: 'The sticker or label looks wrong',
        hint: 'Photograph the sticker close up, including its edges.',
    },
    {
        value: 'signature_mismatch',
        label: 'The signature does not match known exemplars',
        hint: 'Tell us which signer, and what looks off compared to a real example.',
    },
    {
        value: 'cert_not_found',
        label: 'The certificate number does not resolve on the provider site',
        hint: 'A screenshot of the empty provider lookup is the most useful proof here.',
    },
    {
        value: 'record_mismatch',
        label: 'The provider record describes a different item',
        hint: 'Tell us what the record says and what the item actually is.',
    },
    {
        value: 'serial_seller',
        label: 'The seller lists many suspicious items',
        hint: 'A link to the seller page matters more than a photo for this one.',
    },
    {
        value: 'other',
        label: 'Something else',
        hint: 'Explain it in the details box below - this one always needs a written explanation.',
    },
];

const label = (list: readonly ReportReasonOption[], value: unknown): string | null =>
    list.find((option) => option.value === String(value ?? ''))?.label ?? null;

export const genuineClaimReasonLabel = (value: unknown): string | null => label(GENUINE_CLAIM_REASONS, value);
export const fakeReportReasonLabel = (value: unknown): string | null => label(FAKE_REPORT_REASONS, value);

/** Either vocabulary, for admin surfaces that show whichever one is set. */
export const reportReasonLabel = (value: unknown): string | null =>
    genuineClaimReasonLabel(value) ?? fakeReportReasonLabel(value);
